import React from 'react';
import styled from 'styled-components';
import { useChat } from '../../contexts/ChatProvider';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';


export const Content = styled.div`
  text-align: left;
  vertical-align: text-top;
  font-size: 14px;
  align-self: flex-start;
  line-height: auto;
  color: ${({ color }) => color || '#000000'};
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  position: relative;
  box-sizing: border-box;
  word-wrap: break-word;
  width: 100%;
  max-width: ${({ isFullScreen }) => (isFullScreen ? "800px" : "272px")};

  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${({ linkColor }) => linkColor || 'blue'};
  }

  .div1 {
    width: 100%;
  }
  .pre1 {
    background-color: ${({ theme }) => theme || '#FFF'};
    border-radius: 5px;
    padding: 5px;
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100%;
  }
  .code1 {
    font-size: 12px;
    display: block;
    text-align: left;
  }
  .singleBackTick {
    background-color: ${({ theme }) => theme || '#FFF'};
    font-size: 12px;
    display: inline;
    text-align: left;
    border-radius: 5px;
    padding: 3px 5px;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 18px;
  }

  ul > li {
    list-style: disc;
  }

  ol > li {
    list-style: decimal;
  }

  /* nested lists: tighten spacing and keep the right marker per type */
  li > ul,
  li > ol {
    margin: 4px 0;
  }

  /* GFM task lists: hide the bullet, since the checkbox is the marker */
  li:has(> input[type="checkbox"]) {
    list-style: none;
    margin-left: -18px;
  }

  h1 {
    font-size: 20px;
    font-weight: 700;
    margin: 12px 0 8px 0;
    line-height: 1.3;
  }
  h2 {
    font-size: 17px;
    font-weight: 700;
    margin: 10px 0 6px 0;
    line-height: 1.3;
  }
  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 8px 0 6px 0;
    line-height: 1.3;
  }
  h4, h5, h6 {
    font-size: 14px;
    font-weight: 600;
    margin: 8px 0 4px 0;
    line-height: 1.3;
  }

  img {
    display: block;
    margin: 0 auto;
    padding-top: 10px;
    width: 80%;
    height: auto;
  }

  hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 8px 0;
  }
  .table-wrapper {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 8px 0;

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme || '#bbb'} transparent;

    /* Chrome, Safari, Edge (WebKit) */
    &::-webkit-scrollbar {
      height: 6px; /* horizontal scrollbar thickness */
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${({ tableBorderColor }) => tableBorderColor || '#bbb'};
      border-radius: 4px;
    }
  }

  table {
    border-collapse: collapse;
    border: 1px solid ${({ tableBorderColor }) => tableBorderColor || '#ddd'};
    font-size: 13px;
  }

  th, td {
    border: 1px solid ${({ tableBorderColor }) => tableBorderColor || '#ddd'};
    padding: 6px 10px;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    min-width: 80px;
  }

  th {
    font-weight: 600;
    background-color: ${({ theme }) => theme || '#f5f5f5'};
  }
  blockquote {
    margin: 8px 0;
    padding: 4px 12px;
    border-left: 3px solid ${({ linkColor }) => linkColor || '#999'};
    color: ${({ color }) => color || '#000000'};
    opacity: 0.85;
    font-style: italic;

    p {
      margin: 0 0 4px 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  }

  @media only screen and (min-width: 768.01px) {
    .pre1 {
      width: auto;
      max-width: ${({ isFullScreen }) => (isFullScreen ? "800px" : "230px")};
    }
  }
  @media only screen and (max-width: 360px) {
    .pre1 {
      width: auto;
      max-width: 202px;
    }
  }
`;


const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...defaultSchema.tagNames, 'figure', 'figcaption'],
  attributes: {
    ...defaultSchema.attributes,
    img: [...(defaultSchema.attributes?.img || []), 'src', 'alt', 'title', 'height', 'width', 'loading', 'style'],
    '*': [...(defaultSchema.attributes?.['*'] || []), 'class', 'data-section-id', 'data-dataset-id'],
  },
};

const fixMalformedImgSrc = (text) => {
  return text.replace(
    /src=(['"])\[.*?\]\((https?:\/\/[^)]+)\)\1/g,
    (match, quote, realUrl) => `src=${quote}${realUrl}${quote}`
  );
};

const matchesPattern = (href, pattern) => {
  if (!href || !pattern) return false;
  try {
    const { pathname } = new URL(href, window.location.origin);
    return pathname === pattern || pathname.startsWith(`${pattern.replace(/\/$/, "")}/`);
  } catch {
    return false;
  }
};

export default function TextContent({ linkColor, color, created_at, children = '', isFullScreen = false }) {
  const { theme, disableAfterMS, disableUrlPattern, shoji } = useChat();

  const createdTime = created_at ? new Date(created_at).getTime() : NaN;
  const expiresAt = Number.isFinite(createdTime) ? createdTime + disableAfterMS : NaN;
  const [isExpired, setIsExpired] = React.useState(
    () => Number.isFinite(expiresAt) && Date.now() >= expiresAt
  );

  React.useEffect(() => {
    if (!Number.isFinite(expiresAt)) return;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      setIsExpired(true);
      return;
    }
    // clamp: setTimeout overflows and fires immediately above 2^31-1 ms
    const id = setTimeout(() => setIsExpired(true), Math.min(remaining, 2 ** 31 - 1));
    return () => clearTimeout(id);
  }, [expiresAt]);

  const components = {
    code({ className, children: codeChildren, ...props }) {
      const codeText = String(codeChildren).replace(/\n$/, '');
      return (
        <code className="singleBackTick" {...props}>
          {codeText}
        </code>
      );
    },
    pre({ children: preChildren, ...props }) {
      const codeElement = React.Children.toArray(preChildren)[0];
      const rawClassName = codeElement?.props?.className || '';
      const langMatch = /language-(\w+)/.exec(rawClassName);
      const codeText = String(codeElement?.props?.children || '').replace(/\n$/, '');

      return (
        <div className="div1">
          <pre className="pre1" {...props}>
            <code className={`code1 lang-${langMatch ? langMatch[1] : 'text'}`}>
              {codeText}
            </code>
          </pre>
        </div>
      );
    },

    a({ href, children: linkChildren, ...props }) {
      const link = (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {linkChildren}
        </a>
      );

      if (!shoji() || !isExpired || !matchesPattern(href, disableUrlPattern)) {
        return link;
      }

      return (
        <span
          aria-disabled="true"
          title="This link has expired"
          style={{
            opacity: 0.5,
            cursor: "not-allowed",
            pointerEvents: "none",
            textDecoration: "line-through",
          }}
        >
          {linkChildren}
        </span>
      );
    },
    img({ src, alt, ...props }) {
      if (!src || !/^https?:\/\//.test(src)) return null;
      return (<img src={src} alt={alt} {...props} />);
    },
    li({ children: liChildren, ...props }) {
      return (
        <li {...props}>
          {liChildren}
        </li>
      );
    },
    table({ children: tableChildren, ...props }) {
      return (
        <div className="table-wrapper">
          <table {...props}>{tableChildren}</table>
        </div>
      );
    },
    th({ children: thChildren, style, ...props }) {
      return <th {...props}>{thChildren}</th>;
    },
    td({ children: tdChildren, style, ...props }) {
      return <td {...props}>{tdChildren}</td>;
    },
  };

  return (
    <Content
      isFullScreen={isFullScreen}
      linkColor={linkColor}
      color={color}
      theme={theme}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        remarkRehypeOptions={{ allowDangerousHtml: true }}
        components={components}
      >
        {fixMalformedImgSrc(children)}
      </Markdown>
    </Content>
  );
}